// src/serviceWorkerRegistration.js
// Manejo de registro del service worker y suscripción Push
import { snoozeNotification } from "./api/apiClient";

const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_VAPID_PUBLIC || "<REPLACE_WITH_PUBLIC_VAPID_KEY>";

function urlBase64ToUint8Array(base64String) {
  if (!base64String) {
    throw new Error(
      "VAPID public key is empty. Make sure REACT_APP_VAPID_PUBLIC is set in your .env and restart the dev server."
    );
  }

  // Sanitize: remove surrounding quotes, whitespace/newlines
  let base64 = String(base64String)
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .replace(/\s+/g, "");

  // Convert base64url to base64
  base64 = base64.replace(/-/g, "+").replace(/_/g, "/");

  // Pad to multiple of 4
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  base64 = base64 + pad;

  // (debug logs removed)

  let rawData;
  try {
    rawData = atob(base64);
  } catch (err) {
    throw new Error(
      "Failed to decode VAPID public key. Ensure it is base64url (no quotes/newlines). Original error: " +
        err.message
    );
  }

  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Setup global message listener for service worker messages
// This needs to be outside registerAndSubscribe to handle messages at any time
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", async (event) => {
    if (!event.data) return;

    // Handle notification snooze
    if (event.data.type === "SNOOZE_NOTIFICATION") {
      const { minutes, notification } = event.data;

      console.log(`Processing snooze for ${minutes} minutes`, notification);

      try {
        // Call API to snooze notification
        const result = await snoozeNotification({
          subscription: notification.data?.subscription || 0,
          snoozeMinutes: minutes,
          title: notification.title || "",
          body: notification.body || "",
          data: JSON.stringify(notification.data || {}),
        });

        if (result.success) {
          console.log(`Notification snoozed successfully for ${minutes} minutes`);
        } else {
          console.error("Failed to snooze notification:", result.message);
        }
      } catch (error) {
        console.error("Error snoozing notification:", error);
      }
    }

    // Handle subscription renewal (this will be handled per registration)
    if (event.data.type === "RENEW_SUBSCRIPTION") {
      console.log("Subscription renewal requested");
      // This will be handled when there's an active registration
    }
  });
}

export async function registerAndSubscribe({
  saveSubscriptionUrl = `${process.env.REACT_APP_API_URL}/api/v1/PushNotification/save-subscription`,
  onSuccess,
  onError,
  userId,
} = {}) {
  // Require userId before attempting registration
  if (!userId) {
    const err = new Error(
      "User id is not available. Cannot register for push notifications."
    );
    if (onError) onError(err);
    throw err;
  }
  // Append userId as route parameter instead of query string
  saveSubscriptionUrl = `${saveSubscriptionUrl}/${encodeURIComponent(userId)}`;

  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker no soportado");
  }
  if (!("PushManager" in window)) {
    throw new Error("Push no soportado");
  }

  const registration = await navigator.serviceWorker.register("/sw.js");

  // Handle subscription renewal specific to this registration
  const handleRenewal = (event) => {
    if (event.data && event.data.type === "RENEW_SUBSCRIPTION") {
      subscribeAndSend(registration, saveSubscriptionUrl).catch(console.error);
    }
  };

  navigator.serviceWorker.addEventListener("message", handleRenewal);

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permiso de notificaciones denegado");
  }

  const result = await subscribeAndSend(registration, saveSubscriptionUrl);
  if (onSuccess) onSuccess(result);
  return result;
}

async function subscribeAndSend(registration, saveSubscriptionUrl) {
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  // Enviar subscription al servidor
  await fetch(saveSubscriptionUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(subscription),
    credentials: "include",
  });

  return { registration, subscription };
}

export async function unsubscribe({
  removeSubscriptionUrl = `${process.env.REACT_APP_API_URL}/api/v1/PushNotification/remove-subscription`,
  userId,
} = {}) {
  if (!userId) {
    throw new Error("User id is required to unsubscribe");
  }
  // Append userId as route parameter instead of query string
  removeSubscriptionUrl = `${removeSubscriptionUrl}/${encodeURIComponent(
    userId
  )}`;

  const registration = await navigator.serviceWorker.ready;
  const sub = await registration.pushManager.getSubscription();
  if (!sub) return;
  await sub.unsubscribe();
  await fetch(removeSubscriptionUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sub),
    credentials: "include",
  });
}

// Polling fallback: post message al SW para mostrar notificaciones
export async function startPollingFallback(
  apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/PushNotification/notifications`,
  intervalMs = 30000
) {
  if (!("serviceWorker" in navigator))
    throw new Error("Service Worker no soportado");
  if (Notification.permission !== "granted") {
    await Notification.requestPermission();
    if (Notification.permission !== "granted")
      throw new Error("Permiso de notificaciones denegado");
  }
  const swReg = await navigator.serviceWorker.ready;
  let lastSeenId = null;

  const poll = async () => {
    try {
      const res = await fetch(apiUrl, { credentials: "include" });
      if (!res.ok) return;
      const items = await res.json();
      for (let i = items.length - 1; i >= 0; i--) {
        const it = items[i];
        if (lastSeenId === null || it.id > lastSeenId) {
          const msg = {
            type: "SHOW_NOTIFICATION",
            title: it.title || "Notificación",
            options: {
              body: it.body || "",
              icon: it.icon || "/logo192.png",
              data: { url: it.url },
            },
          };
          if (swReg.active) swReg.active.postMessage(msg);
          else if (swReg.waiting) swReg.waiting.postMessage(msg);
          lastSeenId = it.id;
        }
      }
      if (items.length && lastSeenId === null) lastSeenId = items[0].id;
    } catch (err) {
      console.warn("Polling error", err);
    }
  };

  poll();
  const handle = setInterval(poll, intervalMs);
  return () => clearInterval(handle);
}
